// Typed API client. All requests go through Vite's dev proxy in dev,
// or directly to the worker URL in production builds.
//
// credentials: 'include' is required so the Cloudflare Access cookie
// travels with each request once auth is wired up in production.

import type {
  AppRole,
  DashboardTodayResponse,
  Sku,
  SkuCreateInput,
  CountCorrectionInput,
  Customer,
  CustomerCreateInput,
  CustomerUpdateInput,
  JobListRow,
  JobDetail,
  JobDocument,
  JobCreateInput,
  JobUpdateInput,
  InventoryListResponse,
  InventoryTxnRow,
  ReceiveDeliveryInput,
  SkuCategory,
  SkuCategoryCreateInput,
  PricingInputBody,
  PricingResult,
  JobMiscLine,
  MiscLineCreateInput,
  CrewRow,
  CalendarAssignmentRow,
  CrewCreateInput,
  CrewUpdateInput,
  JobAssignmentCreateInput,
  JobAssignmentUpdateInput,
  JobTemplate,
  JobTemplateCreateInput,
  QuoteCreateInput,
  CalendarUnassignedResponse,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
  WarrantyVisitRow,
  TaskType,
  TimeEntry,
  TimeEntryCreateInput,
  TimeEntryUpdateInput,
  InstallAssignmentFootage,
  AssignmentFootageUpdateInput,
  CrewUtilizationResponse,
  MonthlyFootagePoint,
  CrewPersonDetail,
} from '@gutterinv/shared';
import { runBatchedBulkActive } from '../lib/bulk-active.js';

export type { QuoteCreateInput };

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
}

export interface MeResponse {
  userId: string;
  email: string;
  displayName: string | null;
  role: AppRole;
  tenant: TenantInfo | null;
  env: string;
}

export interface TenantSettingsUpdateInput {
  name?: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  nextPoNumber?: number;
  /** Customer labor CHARGE rates (migration 0054), integer cents per unit. */
  gutterLaborRateCentsPerLf?: number;
  insulationLaborRateCentsPerSf?: number;
  /** Labor COST rates (migration 0072), integer cents per unit (for margin). */
  gutterLaborCostCentsPerLf?: number;
  insulationLaborCostCentsPerSf?: number;
}

export function patchTenantSettings(
  input: TenantSettingsUpdateInput,
): Promise<{ ok: boolean }> {
  return apiPatch<{ ok: boolean }>('/api/tenant/settings', input);
}

export async function uploadTenantLogo(file: Blob): Promise<{ logoUrl: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetchWithAuthRetry('/api/tenant/logo', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as { logoUrl: string };
}

/** Error thrown by the JSON API helpers. Carries the server's machine-readable
 *  `code` (for branching) and HTTP `status` alongside a human `message`. */
export interface ApiError extends Error {
  code?: string;
  status: number;
}

/**
 * Build a friendly Error from a non-ok response. Handles BOTH server error
 * shapes: `{error:{code,message}}` (nested) and `{error:'msg', code}` (flat).
 * The thrown Error's `.message` is the server's human text when present (so the
 * UI shows that, not a raw "PATCH … failed: 400 …" string), and `.code` carries
 * the machine-readable code — callers branch on `err.code`, never on `.message`.
 */
async function buildApiError(method: string, path: string, res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let code: string | undefined;
  let friendly: string | undefined;
  try {
    const body = JSON.parse(text) as {
      error?: unknown;
      code?: unknown;
      message?: unknown;
    };
    if (body && typeof body.error === 'object' && body.error !== null) {
      const e = body.error as { code?: unknown; message?: unknown };
      if (typeof e.code === 'string') code = e.code;
      if (typeof e.message === 'string') friendly = e.message;
    } else if (typeof body.error === 'string') {
      friendly = body.error;
    }
    if (!code && typeof body.code === 'string') code = body.code;
    if (!friendly && typeof body.message === 'string') friendly = body.message;
  } catch {
    // Non-JSON body — fall back to the raw string below.
  }
  const err = new Error(
    friendly ?? `${method} ${path} failed: ${res.status} ${res.statusText} ${text}`,
  ) as ApiError;
  err.code = code;
  err.status = res.status;
  return err;
}

// Single retry on 401. A 401 is returned by the auth middleware BEFORE the
// route handler runs (verified: no handler emits 401 after a write), so the
// request had no side effect and is safe to replay — even POST/PATCH/DELETE.
// When the 1-hour access-token cookie has just expired, a sibling request's
// server-side refresh lands a fresh cookie within a moment; the brief wait +
// one retry lets THIS request ride that refreshed cookie instead of bouncing
// the whole app to Login. Only a SECOND 401 surfaces as a real auth failure.
async function fetchWithAuthRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status !== 401) return res;
  await new Promise((resolve) => setTimeout(resolve, 350));
  return fetch(input, init);
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetchWithAuthRetry(path, { credentials: 'include' });
  if (!res.ok) {
    throw await buildApiError('GET', path, res);
  }
  return (await res.json()) as T;
}

/**
 * Detect whether an unknown error originated from a 401 response. Used
 * by App.tsx to gate on the Supabase session and by main.tsx's global
 * cache subscriber to invalidate the me query on any 401, so the
 * bounce-to-Login fires immediately rather than waiting for the next
 * stale-time tick.
 */
export function is401Error(error: unknown): boolean {
  // Prefer the structured status (buildApiError attaches it); fall back to the
  // legacy message regex for any error built the old way.
  if (error && typeof error === 'object' && (error as { status?: number }).status === 401) {
    return true;
  }
  return (
    error instanceof Error &&
    /\b401\b|\bunauthenticated\b/i.test(error.message)
  );
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuthRetry(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await buildApiError('POST', path, res);
  }
  return (await res.json()) as T;
}

async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuthRetry(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await buildApiError('PATCH', path, res);
  }
  return (await res.json()) as T;
}

export function getMe(): Promise<MeResponse> {
  return apiGet<MeResponse>('/api/me');
}

/** Browser-local calendar date as YYYY-MM-DD (the office's "today"). */
function localTodayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getDashboardToday(): Promise<DashboardTodayResponse> {
  // Send the browser-local date so "today" matches the office's timezone,
  // not the Postgres server's UTC date. Computed per call, so the 10s poll
  // rolls over correctly at local midnight.
  return apiGet<DashboardTodayResponse>(`/api/dashboard/today?today=${localTodayISO()}`);
}

export interface CountCorrectionResponse {
  sku: Sku;
  delta: number;
  previousOnHand: number;
}

export function postCountCorrection(
  input: CountCorrectionInput,
): Promise<CountCorrectionResponse> {
  return apiPost<CountCorrectionResponse>('/api/inventory/count-correction', input);
}

// === Customers ===

export function getCustomers(): Promise<Customer[]> {
  return apiGet<Customer[]>('/api/customers');
}

export function postCustomer(input: CustomerCreateInput): Promise<Customer> {
  return apiPost<Customer>('/api/customers', input);
}

export function patchCustomer(
  id: string,
  input: CustomerUpdateInput,
): Promise<Customer> {
  return apiPatch<Customer>(`/api/customers/${id}`, input);
}

export async function deleteCustomer(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/customers/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 409) {
      throw new Error('Cannot delete — customer still has jobs');
    }
    throw new Error(`DELETE failed: ${res.status} ${res.statusText} ${text}`);
  }
}

// === Jobs ===

export function getJobs(): Promise<JobListRow[]> {
  return apiGet<JobListRow[]>('/api/jobs');
}

export function getJobDetail(id: string): Promise<JobDetail> {
  return apiGet<JobDetail>(`/api/jobs/${id}`);
}

export function postJob(input: JobCreateInput): Promise<JobListRow> {
  return apiPost<JobListRow>('/api/jobs', input);
}

export function patchJob(id: string, input: JobUpdateInput): Promise<JobListRow> {
  return apiPatch<JobListRow>(`/api/jobs/${id}`, input);
}

/** Record/edit the job's QBO invoice # and/or invoiced amount (cents).
 *  Either field may be omitted (untouched); null clears. Migration 0059. */
export function setJobInvoice(
  jobId: string,
  input: { invoiceNumber?: string | null; amountCents?: number | null },
): Promise<{ updated: boolean; externalInvoiceNumber: string | null; invoicedAmountCents: number | null }> {
  return apiPatch(`/api/jobs/${jobId}/invoice`, input);
}

export async function deleteJob(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/jobs/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    // Surface the server's machine-readable message (e.g. the 409
    // "past Quoted — disqualify instead") rather than a raw status dump.
    throw await buildApiError('DELETE', `/api/jobs/${id}`, res);
  }
}

// === Tenant settings (PO numbering, aging thresholds) ===

export function getNextPo(): Promise<{ nextPoNumber: number }> {
  return apiGet<{ nextPoNumber: number }>('/api/tenant/next-po');
}

export interface TenantSettingsResponse {
  nextPoNumber: number;
  /** Customer labor CHARGE rates (migration 0054), integer cents per unit. */
  gutterLaborRateCentsPerLf: number;
  insulationLaborRateCentsPerSf: number;
  /** Labor COST rates (migration 0072), integer cents per unit (for margin). */
  gutterLaborCostCentsPerLf: number;
  insulationLaborCostCentsPerSf: number;
}

export function getTenantSettings(): Promise<TenantSettingsResponse> {
  return apiGet<TenantSettingsResponse>('/api/tenant/settings');
}


// === SKU Categories (Settings page) ===

export function getSkuCategories(): Promise<SkuCategory[]> {
  return apiGet<SkuCategory[]>('/api/sku-categories');
}

export function postSkuCategory(input: SkuCategoryCreateInput): Promise<SkuCategory> {
  return apiPost<SkuCategory>('/api/sku-categories', input);
}

export function patchSkuCategory(
  id: string,
  input: Partial<SkuCategoryCreateInput>,
): Promise<SkuCategory> {
  return apiPatch<SkuCategory>(`/api/sku-categories/${id}`, input);
}

// === Pricing ===

export function postPricingPreview(input: PricingInputBody): Promise<PricingResult> {
  return apiPost<PricingResult>('/api/pricing/preview', input);
}

export async function deleteSkuCategory(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/sku-categories/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`DELETE failed: ${res.status} ${res.statusText} ${text}`);
  }
}

export function reorderSkuCategories(ids: string[]): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>('/api/sku-categories/reorder', { ids });
}

export function reorderCrews(ids: string[]): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>('/api/crews/reorder', { ids });
}

export function reorderTemplates(ids: string[]): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>('/api/templates/reorder', { ids });
}

// === SKUs (raw) ===

export function getSkus(opts: { includeInactive?: boolean } = {}): Promise<Sku[]> {
  const qs = opts.includeInactive ? '?includeInactive=true' : '';
  return apiGet<Sku[]>(`/api/skus${qs}`);
}

export function patchSku(id: string, input: Record<string, unknown>): Promise<Sku> {
  return apiPatch<Sku>(`/api/skus/${id}`, input);
}

/** Flip `active` for many SKUs. The server caps each request at 500 ids
 *  (bulkSkuActiveSchema), so we split into sequential ≤500 batches client-side
 *  and aggregate — a 606-id "Enable selected" no longer 400s + fails silently.
 *  A batch failure throws BulkActiveBatchError (which batch + how many already
 *  succeeded) so the caller can surface it. Returns total rows flipped. */
export function bulkPatchSkusActive(ids: string[], active: boolean): Promise<{ updated: number }> {
  return runBatchedBulkActive(ids, active, (batchIds, batchActive) =>
    apiPatch<{ updated: number }>('/api/skus/bulk-active', { ids: batchIds, active: batchActive }),
  );
}

export function postSku(input: SkuCreateInput): Promise<Sku> {
  return apiPost<Sku>('/api/skus', input);
}

/** Distinct existing spec-field values, scoped to ONE category, to seed the
 *  category-aware New SKU form's dropdowns/comboboxes. Keyed by generic field name. */
export function getSkuSpecOptions(categoryId: string): Promise<Record<string, string[]>> {
  return apiGet<Record<string, string[]>>(
    `/api/skus/spec-options?categoryId=${encodeURIComponent(categoryId)}`,
  );
}

// === Inventory ===

export function getInventorySkus(
  opts: { includeInactive?: boolean } = {},
): Promise<InventoryListResponse> {
  const qs = opts.includeInactive ? '?includeInactive=true' : '';
  return apiGet<InventoryListResponse>(`/api/inventory/skus${qs}`);
}

export function getInventoryTxns(skuId: string, limit = 50): Promise<InventoryTxnRow[]> {
  return apiGet<InventoryTxnRow[]>(
    `/api/inventory/txns?skuId=${encodeURIComponent(skuId)}&limit=${limit}`,
  );
}

// === Inventory commitments (per-SKU job breakdown) ===

export interface SkuCommitmentRow {
  jobId: string;
  jobDisplayName: string;
  customerName: string;
  quantity: number;
  status: string;
  createdAt: string;
}

export function getSkuCommitments(skuId: string): Promise<SkuCommitmentRow[]> {
  return apiGet<SkuCommitmentRow[]>(`/api/inventory/commitments?skuId=${encodeURIComponent(skuId)}`);
}

export interface ReceiveDeliveryResponse {
  sku: Sku;
  previousOnHand: number;
  delta: number;
}

export function postReceiveDelivery(
  input: ReceiveDeliveryInput,
): Promise<ReceiveDeliveryResponse> {
  return apiPost<ReceiveDeliveryResponse>('/api/inventory/receive', input);
}

// === Quote creation ===

export interface QuoteCreateResponse {
  jobId: string;
  quoteAmountCents: number;
  lineItemCount: number;
}

export function postQuoteCreate(input: QuoteCreateInput): Promise<QuoteCreateResponse> {
  return apiPost<QuoteCreateResponse>('/api/quotes/create', input);
}

/** Wipe a saved job's quote BoM back to $0 (deletes quote line items, nulls
 *  the quote amount, reverts estimated/quoted → created). 409 if accepted+. */
export function clearQuote(jobId: string): Promise<{ jobId: string; quoteAmountCents: null }> {
  return apiPost<{ jobId: string; quoteAmountCents: null }>(`/api/quotes/${jobId}/clear`, {});
}

// === Miscellaneous quote lines (free-form, migration 0075) ===
// Append/remove a flat misc line on ANY non-completed job (incl. accepted+). This path NEVER
// re-prices the SKU BoM — the frozen install contract stays byte-identical.

export function postMiscLine(jobId: string, input: MiscLineCreateInput): Promise<JobMiscLine> {
  return apiPost<JobMiscLine>(`/api/quotes/${jobId}/misc-lines`, input);
}

export async function deleteMiscLine(jobId: string, lineId: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/quotes/${jobId}/misc-lines/${lineId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`DELETE failed: ${res.status} ${res.statusText} ${text}`);
  }
}

// === Job Sketch ===

export function getJobSketchUrl(jobId: string): string {
  return `/api/jobs/${jobId}/sketch`;
}

// === Job PDF ===

export function generateJobPdf(jobId: string): Promise<{ generated: boolean }> {
  return apiPost<{ generated: boolean }>(`/api/job-pdf/${jobId}/generate`, {});
}

export function getJobPdfUrl(jobId: string, version?: 'draft' | 'frozen'): string {
  const v = version ? `?version=${version}` : '';
  return `/api/job-pdf/${jobId}${v}`;
}

// === Job Documents (per-job library; R2-backed) ===

export function getJobDocuments(jobId: string): Promise<JobDocument[]> {
  return apiGet<JobDocument[]>(`/api/jobs/${jobId}/documents`);
}

export async function uploadJobDocument(
  jobId: string,
  file: File,
  label?: string,
): Promise<JobDocument> {
  const fd = new FormData();
  fd.append('file', file);
  if (label && label.trim()) fd.append('label', label.trim());
  const res = await fetchWithAuthRetry(`/api/jobs/${jobId}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: fd, // no Content-Type — browser sets the multipart boundary
  });
  if (!res.ok) throw await buildApiError('POST', `/api/jobs/${jobId}/documents`, res);
  return (await res.json()) as JobDocument;
}

export async function deleteJobDocument(jobId: string, docId: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/jobs/${jobId}/documents/${docId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw await buildApiError('DELETE', `/api/jobs/${jobId}/documents/${docId}`, res);
}

/** Path for <a href> / <iframe src> / <img src> — browser sends the auth
 *  cookie. The worker streams the file from R2 (inline for pdf/images,
 *  attachment for docx/xlsx). */
export function getJobDocumentUrl(jobId: string, docId: string): string {
  return `/api/jobs/${jobId}/documents/${docId}`;
}

// === Job Time Entries (per-job crew hours; migration 0056, office-only) ===

export function getJobTimeEntries(jobId: string): Promise<TimeEntry[]> {
  return apiGet<TimeEntry[]>(`/api/jobs/${jobId}/time-entries`);
}

export function createJobTimeEntry(
  jobId: string,
  input: TimeEntryCreateInput,
): Promise<TimeEntry> {
  return apiPost<TimeEntry>(`/api/jobs/${jobId}/time-entries`, input);
}

export function patchJobTimeEntry(
  jobId: string,
  entryId: string,
  input: TimeEntryUpdateInput,
): Promise<TimeEntry> {
  return apiPatch<TimeEntry>(`/api/jobs/${jobId}/time-entries/${entryId}`, input);
}

export async function deleteJobTimeEntry(jobId: string, entryId: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/jobs/${jobId}/time-entries/${entryId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw await buildApiError('DELETE', `/api/jobs/${jobId}/time-entries/${entryId}`, res);
  }
}

// === Per-assignment install footage (migration 0065, office-only) ===

export function getJobInstallAssignments(jobId: string): Promise<InstallAssignmentFootage[]> {
  return apiGet<InstallAssignmentFootage[]>(`/api/jobs/${jobId}/install-assignments`);
}

/** Set one install assignment's actual footage. The worker returns the
 *  trigger-recomputed job-level rollup too (jobActualInstalledFootage). */
export function patchJobAssignmentFootage(
  jobId: string,
  assignmentId: string,
  input: AssignmentFootageUpdateInput,
): Promise<{
  updated: boolean;
  id: string;
  actualInstalledFootage: number | null;
  jobActualInstalledFootage: number | null;
}> {
  return apiPatch(`/api/jobs/${jobId}/assignments/${assignmentId}`, input);
}

// === Crews ===

export function getCrews(): Promise<CrewRow[]> {
  return apiGet<CrewRow[]>('/api/crews');
}

export function postCrew(input: CrewCreateInput): Promise<CrewRow> {
  return apiPost<CrewRow>('/api/crews', input);
}

export function patchCrew(id: string, input: CrewUpdateInput): Promise<CrewRow> {
  return apiPatch<CrewRow>(`/api/crews/${id}`, input);
}

export async function deleteCrew(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/crews/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 409) {
      throw new Error('Cannot delete — crew still has assignments');
    }
    throw new Error(`DELETE failed: ${res.status} ${res.statusText} ${text}`);
  }
}

// === Crew Members (Telegram users → crew assignment) ===

export type CrewMemberRole = 'crew' | 'admin';

/** A preset crew a person belongs to (Phase 2.5: many-to-many). */
export interface CrewPresetRef {
  id: string;
  name: string;
  color: string;
}

export interface CrewMemberRow {
  id: string;
  telegramUserId: string;
  name: string;
  role: CrewMemberRole;
  active: boolean;
  // Representative preset (first of `crews`) — kept for the PeopleMultiSelect
  // color dot + any single-preset display. `crews` is the authoritative set.
  crewId: string | null;
  crewName: string | null;
  crewColor: string | null;
  crews: CrewPresetRef[];
}

export interface CrewMemberPatchInput {
  name?: string;
  role?: CrewMemberRole;
  active?: boolean;
  /** Full preset-crew set (authoritative; replaces all memberships). */
  crewIds?: string[];
}

export interface CrewMemberCreateInput {
  telegramUserId: number;
  name: string;
  role?: CrewMemberRole;
  crewIds?: string[];
}

export function getCrewMembers(): Promise<CrewMemberRow[]> {
  return apiGet<CrewMemberRow[]>('/api/crew-members');
}

/** Phase 2.2: expand a crew preset into its active member person IDs. */
export function peopleFromPreset(crewId: string): Promise<{ personIds: string[] }> {
  return apiPost<{ personIds: string[] }>('/api/people/from-preset', { crewId });
}

export function postCrewMember(input: CrewMemberCreateInput): Promise<CrewMemberRow> {
  return apiPost<CrewMemberRow>('/api/crew-members', input);
}

export function patchCrewMember(
  id: string,
  input: CrewMemberPatchInput,
): Promise<CrewMemberRow> {
  return apiPatch<CrewMemberRow>(`/api/crew-members/${id}`, input);
}

export async function deleteCrewMember(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/crew-members/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 409) {
      // Worker returns a friendlier message in the body; surface it.
      try {
        const body = JSON.parse(text) as { error?: string };
        if (body.error) throw new Error(body.error);
      } catch {
        // fall through to generic message
      }
      throw new Error('Cannot delete — this member has activity. Toggle Active off instead.');
    }
    throw new Error(`DELETE failed: ${res.status} ${res.statusText} ${text}`);
  }
}

// === Crew Utilization analytics (office-only; read-only over time_entries) ===

/** Period payload — week + month per-crew CREDITED rollups + team ACTUAL
 *  totals. Both ranges fetched in one call so the page renders once. */
export function getCrewUtilization(range: {
  weekFrom: string;
  weekTo: string;
  monthFrom: string;
  monthTo: string;
}): Promise<CrewUtilizationResponse> {
  const { weekFrom, weekTo, monthFrom, monthTo } = range;
  return apiGet<CrewUtilizationResponse>(
    `/api/crew-utilization?weekFrom=${weekFrom}&weekTo=${weekTo}&monthFrom=${monthFrom}&monthTo=${monthTo}`,
  );
}

/** Monthly LF/SF chart series for a calendar year (12 points). */
export function getCrewUtilizationSeries(year: number): Promise<MonthlyFootagePoint[]> {
  return apiGet<MonthlyFootagePoint[]>(`/api/crew-utilization/series?year=${year}`);
}

/** One person's jobs + credited totals over a range (drawer drill). */
export function getCrewPersonDetail(
  crewMemberId: string,
  from: string,
  to: string,
): Promise<CrewPersonDetail> {
  return apiGet<CrewPersonDetail>(
    `/api/crew-utilization/person/${crewMemberId}?from=${from}&to=${to}`,
  );
}

// === Calendar ===

export function getCalendarAssignments(from: string, to: string): Promise<CalendarAssignmentRow[]> {
  return apiGet<CalendarAssignmentRow[]>(`/api/calendar/assignments?from=${from}&to=${to}`);
}

export function getCalendarUnassigned(): Promise<CalendarUnassignedResponse> {
  return apiGet<CalendarUnassignedResponse>('/api/calendar/unassigned');
}

export function postCalendarAssignment(input: JobAssignmentCreateInput): Promise<unknown> {
  return apiPost<unknown>('/api/calendar/assignments', input);
}

export function patchCalendarAssignment(
  id: string,
  input: JobAssignmentUpdateInput,
): Promise<unknown> {
  return apiPatch<unknown>(`/api/calendar/assignments/${id}`, input);
}

export async function deleteCalendarAssignment(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/calendar/assignments/${id}`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error('Delete failed');
}

// === Scheduled tasks (non-job calendar items, migration 0050) ===
export interface ScheduledTaskCreateInput {
  taskType: TaskType;
  taskDate: string;
  personIds: string[];
  notes?: string | null;
}
export type ScheduledTaskUpdateInput = Partial<ScheduledTaskCreateInput>;

export function getCalendarTasks(from: string, to: string): Promise<CalendarTaskRow[]> {
  return apiGet<CalendarTaskRow[]>(`/api/calendar/tasks?from=${from}&to=${to}`);
}
export function postScheduledTask(input: ScheduledTaskCreateInput): Promise<{ id: string }> {
  return apiPost<{ id: string }>('/api/calendar/tasks', input);
}
export function patchScheduledTask(
  id: string,
  input: ScheduledTaskUpdateInput,
): Promise<{ id: string }> {
  return apiPatch<{ id: string }>(`/api/calendar/tasks/${id}`, input);
}
export async function deleteScheduledTask(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/calendar/tasks/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Delete failed');
}
export function setScheduledTaskComplete(id: string, done: boolean): Promise<unknown> {
  return apiPost<unknown>(`/api/calendar/tasks/${id}/${done ? 'complete' : 'uncomplete'}`, {});
}

// === Warranty visits (job-linked follow-on visits, migration 0051) ===
export interface WarrantyVisitCreateInput {
  visitDateStart: string;
  visitDateEnd: string;
  personIds: string[];
  notes?: string | null;
}
export type WarrantyVisitUpdateInput = Partial<WarrantyVisitCreateInput>;

/** Calendar feed (3rd feed, parallel to assignments + tasks). */
export function getCalendarWarrantyVisits(
  from: string,
  to: string,
): Promise<CalendarWarrantyVisitRow[]> {
  return apiGet<CalendarWarrantyVisitRow[]>(`/api/calendar/warranty?from=${from}&to=${to}`);
}
/** All warranty visits for one job (JobDetail Warranty card). */
export function getJobWarrantyVisits(jobId: string): Promise<WarrantyVisitRow[]> {
  return apiGet<WarrantyVisitRow[]>(`/api/calendar/warranty/job/${jobId}`);
}
export function postWarrantyVisit(
  jobId: string,
  input: WarrantyVisitCreateInput,
): Promise<{ id: string }> {
  return apiPost<{ id: string }>(`/api/calendar/warranty/job/${jobId}`, input);
}
export function patchWarrantyVisit(
  id: string,
  input: WarrantyVisitUpdateInput,
): Promise<{ id: string }> {
  return apiPatch<{ id: string }>(`/api/calendar/warranty/${id}`, input);
}
export async function deleteWarrantyVisit(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/calendar/warranty/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Delete failed');
}
export function setWarrantyVisitComplete(id: string, done: boolean): Promise<unknown> {
  return apiPost<unknown>(`/api/calendar/warranty/${id}/${done ? 'complete' : 'uncomplete'}`, {});
}
/** Estimate-purpose calendar assignment completion — the office's "crew did
 *  the measure visit" marker. Worker rejects non-estimate / post-install. */
export function setAssignmentComplete(id: string, done: boolean): Promise<unknown> {
  return apiPost<unknown>(`/api/calendar/assignments/${id}/${done ? 'complete' : 'uncomplete'}`, {});
}

// === Job Templates (Manage Templates drawer + New Job dialog) ===

export function getJobTemplates(): Promise<JobTemplate[]> {
  return apiGet<JobTemplate[]>('/api/templates');
}

export function createJobTemplate(input: JobTemplateCreateInput): Promise<JobTemplate> {
  return apiPost<JobTemplate>('/api/templates', input);
}

export interface TemplatePatchInput {
  name?: string;
  fixedPriceCents?: number;
  displayOrder?: number;
  active?: boolean;
}

export function patchJobTemplate(id: string, input: TemplatePatchInput): Promise<JobTemplate> {
  return apiPatch<JobTemplate>(`/api/templates/${id}`, input);
}

export async function deleteJobTemplate(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/templates/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`DELETE failed: ${res.status} ${text}`);
  }
}

// === Financials (owner-only) ===

async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuthRetry(path, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await buildApiError('PUT', path, res);
  }
  return (await res.json()) as T;
}

export interface FinancialsResponseClient {
  year: number;
  annual: Record<string, number | null>;
  monthly_targets: Record<string, number | null>;
  monthly_by_date: Record<string, Record<string, number | null>>;
}

export function getFinancials(year: number): Promise<FinancialsResponseClient> {
  return apiGet<FinancialsResponseClient>(`/api/financials?year=${year}`);
}

export function putFinancialsAnnual(
  year: number,
  payload: {
    annual: Record<string, number | null>;
    monthly_targets: Record<string, number | null>;
  },
): Promise<{ ok: boolean }> {
  return apiPut<{ ok: boolean }>(`/api/financials/annual?year=${year}`, payload);
}

export function putFinancialsMonthly(
  monthIso: string,
  payload: { data: Record<string, number | null> },
): Promise<{ ok: boolean }> {
  return apiPut<{ ok: boolean }>(`/api/financials/monthly?month=${monthIso}`, payload);
}

// === Feedback (Settings page inbox) ===

export interface FeedbackItem {
  id: string;
  authorUserId: string | null;
  authorDisplayName: string; // server-side COALESCE returns 'Unknown' if null
  body: string;
  completed: boolean;
  completedByUserId: string | null;
  completedByDisplayName: string | null;
  completedAt: string | null;
  createdAt: string;
  noteCount: number; // count of feedback_notes for this item
}

export interface FeedbackNote {
  id: string;
  feedbackItemId: string;
  authorUserId: string | null;
  authorDisplayName: string; // server-side COALESCE
  body: string;
  createdAt: string;
  updatedAt: string;
}

export function getFeedback(): Promise<FeedbackItem[]> {
  return apiGet<FeedbackItem[]>('/api/feedback');
}

export function postFeedback(input: { body: string }): Promise<FeedbackItem> {
  return apiPost<FeedbackItem>('/api/feedback', input);
}

export function patchFeedback(
  id: string,
  input: { completed?: boolean; body?: string },
): Promise<FeedbackItem> {
  return apiPatch<FeedbackItem>(`/api/feedback/${id}`, input);
}

export async function deleteFeedback(id: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/feedback/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`DELETE /api/feedback/${id}: ${res.status}`);
  }
}

export function getFeedbackNotes(itemId: string): Promise<FeedbackNote[]> {
  return apiGet<FeedbackNote[]>(`/api/feedback/${itemId}/notes`);
}

export function postFeedbackNote(
  itemId: string,
  input: { body: string },
): Promise<FeedbackNote> {
  return apiPost<FeedbackNote>(`/api/feedback/${itemId}/notes`, input);
}

export function patchFeedbackNote(
  itemId: string,
  noteId: string,
  input: { body: string },
): Promise<FeedbackNote> {
  return apiPatch<FeedbackNote>(`/api/feedback/${itemId}/notes/${noteId}`, input);
}

export async function deleteFeedbackNote(itemId: string, noteId: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/feedback/${itemId}/notes/${noteId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`DELETE /api/feedback/${itemId}/notes/${noteId}: ${res.status}`);
  }
}

// ============================================================
// Phased insulation (migration 0040) — phases + invoices + void.
// All write endpoints accept an Idempotency-Key header; UI passes a
// freshly-generated UUID per submit click so double-clicks are safe.
// ============================================================

export interface PhaseItem {
  id: string;
  fieldEstimateId: string;
  plannedQuantity: number;
  /** LEGACY (pull-removal 2026-06-09): always null for new items; the API
   *  still returns it for historical rows. No UI consumes it anymore. */
  pulledAt: string | null;
  installedAt: string | null;
}

export interface Phase {
  id: string;
  phaseNumber: number;
  /** External (QuickBooks) invoice number the office typed in for this
   *  phase (migration 0052). Null until recorded. */
  externalInvoiceNumber: string | null;
  /** Computed amount for this phase = INSTALLED items × frozen price
   *  (cents). The office reads this, invoices in QuickBooks, then records
   *  the QBO number above. No discount/tax applied. */
  amountCents: number;
  crewId: string;
  crewName: string;
  /** Phase 2.2: people on this phase's assignment (from assignment_people). */
  people: { id: string; name: string }[];
  assignedDateStart: string;
  assignedDateEnd: string;
  completedAt: string | null;
  items: PhaseItem[];
}

export interface PoolItem {
  fieldEstimateId: string;
  skuId: string | null;
  description: string;
  unit: string;
  totalQuantity: number;
  installedQuantity: number;
  plannedPending: number;
  available: number;
}

export interface PhasesResponse {
  phases: Phase[];
  remainingPool: PoolItem[];
}

export function getJobPhases(jobId: string): Promise<PhasesResponse> {
  return apiGet<PhasesResponse>(`/api/jobs/${jobId}/phases`);
}

export interface CreatePhaseInput {
  /** Legacy; Phase 2.2 sends personIds instead. One of the two required. */
  crewId?: string;
  personIds?: string[];
  assignedDateStart: string;
  assignedDateEnd: string;
  items: Array<{ fieldEstimateId: string; plannedQuantity: number }>;
}

async function apiPostIdem<T>(path: string, body: unknown, idempotencyKey?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const res = await fetchWithAuthRetry(path, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await buildApiError('POST', path, res);
  }
  return (await res.json()) as T;
}

async function apiPatchIdem<T>(path: string, body: unknown, idempotencyKey?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const res = await fetchWithAuthRetry(path, {
    method: 'PATCH',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await buildApiError('PATCH', path, res);
  }
  return (await res.json()) as T;
}

export function postPhase(
  jobId: string,
  input: CreatePhaseInput,
  idempotencyKey?: string,
): Promise<{ phaseId: string; phaseNumber: number }> {
  return apiPostIdem(`/api/jobs/${jobId}/phases`, input, idempotencyKey);
}

/** Partial PATCH — any subset of {crewId, dates, items}. Pull-removal
 *  (2026-06-09): the only 409 is phase_completed; crew/items/dates are all
 *  editable until completion. Idempotency-Key supported. */
export interface PatchPhaseInput {
  crewId?: string;
  personIds?: string[];
  assignedDateStart?: string;
  assignedDateEnd?: string;
  items?: Array<{ fieldEstimateId: string; plannedQuantity: number }>;
}
export function patchPhase(
  jobId: string,
  phaseId: string,
  input: PatchPhaseInput,
  idempotencyKey?: string,
): Promise<{ updated: boolean; phaseId: string }> {
  return apiPatchIdem(`/api/jobs/${jobId}/phases/${phaseId}`, input, idempotencyKey);
}

export async function deletePhase(jobId: string, phaseId: string): Promise<void> {
  const res = await fetchWithAuthRetry(`/api/jobs/${jobId}/phases/${phaseId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`DELETE /api/jobs/${jobId}/phases/${phaseId}: ${res.status} ${text}`);
  }
}

export function postPhaseComplete(
  jobId: string,
  phaseId: string,
  idempotencyKey?: string,
): Promise<{ completed: boolean; jobInstalled: boolean }> {
  return apiPostIdem(`/api/jobs/${jobId}/phases/${phaseId}/complete`, {}, idempotencyKey);
}

/** Migration 0043: revert a completed phase. Migration 0052 dropped the
 *  in-app invoice, so this is now unconditional — no invoice to void
 *  first. Auto-reverts jobs.status if the earlier complete had flipped it
 *  to 'installed'. */
export function postPhaseUncomplete(
  jobId: string,
  phaseId: string,
  idempotencyKey?: string,
): Promise<{ uncompleted: boolean; jobReverted: boolean }> {
  return apiPostIdem(`/api/jobs/${jobId}/phases/${phaseId}/uncomplete`, {}, idempotencyKey);
}

/** Migration 0052: record (or clear) the external QuickBooks invoice number
 *  the office typed in for an insulation phase. Pass null to clear. The app
 *  no longer generates invoices — this just captures the QBO reference. */
export function setPhaseInvoiceNumber(
  jobId: string,
  phaseId: string,
  invoiceNumber: string | null,
): Promise<{ updated: boolean; phaseId: string; externalInvoiceNumber: string | null }> {
  return apiPatchIdem(`/api/jobs/${jobId}/phases/${phaseId}/invoice-number`, { invoiceNumber });
}
